import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Plus } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { SeverityChip, fmtRel } from "./shared";

export const NewIncidentDialog = ({ events = [], existingEventIds, open, onOpenChange, onCreated }) => {
  const [q, setQ] = useState("");

  const candidates = useMemo(() => {
    const taken = new Set(existingEventIds || []);
    return events
      .filter((e) => !taken.has(e.id) && e.status !== "resolved")
      .filter((e) => !q || `${e.title} ${e.zone_name} ${e.type}`.toLowerCase().includes(q.toLowerCase()))
      .slice(0, 30);
  }, [events, q, existingEventIds]);

  const create = async (ev) => {
    try {
      const r = await http.post("/incidents", { event_id: ev.id });
      toast.success("Incident opened", { description: r.data.title });
      onOpenChange(false);
      onCreated?.(r.data);
    } catch {
      toast.error("Could not open incident");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl border border-border bg-[hsl(var(--surface))] p-0"
        style={{ borderRadius: 2 }}
        data-testid="new-incident-dialog"
      >
        <DialogHeader className="px-4 py-3 border-b border-border">
          <DialogTitle className="font-display font-black uppercase tracking-tight text-base flex items-center gap-2">
            <Plus className="w-4 h-4" /> Open Incident from Event
          </DialogTitle>
        </DialogHeader>
        <div className="p-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter events by title / zone / type…"
              data-testid="new-incident-search"
              className="w-full bg-transparent border border-border pl-7 pr-2 py-1.5 text-xs font-mono focus:outline-none focus:border-[hsl(var(--primary))]"
              style={{ borderRadius: 2 }}
            />
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {candidates.length === 0 && (
            <div className="p-6 text-center text-xs text-muted-foreground">No matching active events.</div>
          )}
          {candidates.map((e) => (
            <button
              key={e.id}
              onClick={() => create(e)}
              data-testid={`new-incident-pick-${e.id}`}
              className="w-full text-left px-3 py-2 border-b border-border hover:bg-[hsl(var(--surface-elevated))] transition-colors block"
            >
              <div className="flex items-center gap-2 mb-0.5">
                <SeverityChip sev={e.severity} />
                <span className="font-display font-bold text-xs uppercase tracking-tight truncate">{e.title}</span>
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">{fmtRel(e.ts)}</span>
              </div>
              <div className="font-mono text-[10px] text-muted-foreground">
                {e.camera_id ?? "—"} · {e.zone_name} · {e.type}
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewIncidentDialog;
